/* Small native motion layer. No dependencies; respects reduced motion. */
(() => {
  if (window.rrMotionStage) return;
  window.rrMotionStage = true;
  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  let paused = reduce.matches;
  try { if (sessionStorage.getItem('rr-motion-paused') === 'true') paused = true; } catch (_) {}
  const videos = new Set();
  const inView = new WeakMap();
  const updateVideos = () => videos.forEach(video => {
    if (!video.isConnected) { videos.delete(video); return; }
    if (paused || document.hidden || inView.get(video) === false) video.pause();
    else video.play().catch(() => {});
  });
  const updateMotion = () => {
    root.classList.toggle('rr-motion-paused', paused);
    document.querySelectorAll('[data-rr-motion-toggle]').forEach(button => {
      button.hidden = false;
      button.setAttribute('aria-pressed', String(paused));
      button.querySelector('[data-rr-motion-label]').textContent = paused ? (button.dataset.rrLabelStart || 'Bewegung starten') : (button.dataset.rrLabelPause || 'Bewegung pausieren');
      button.querySelector('[data-rr-motion-icon]').textContent = paused ? '▷' : 'Ⅱ';
    });
    updateVideos();
  };
  const videoObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => inView.set(entry.target, entry.isIntersecting));
    updateVideos();
  }, { threshold: 0.02 }) : null;
  const sceneObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => entry.target.classList.toggle('rr-motion-offscreen', !entry.isIntersecting));
  }, { threshold: 0.01 }) : null;
  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('rr-in-view');
      revealObserver.unobserve(entry.target);
    });
  }, { threshold: 0.08 }) : null;
  const init = (scope = document) => {
    scope.querySelectorAll('[data-rr-stage], .rr-hero__film').forEach(scene => {
      if (scene.dataset.rrSceneReady) return;
      scene.dataset.rrSceneReady = 'true';
      sceneObserver?.observe(scene);
    });
    scope.querySelectorAll('[data-rr-video]').forEach(video => {
      if (videos.has(video)) return;
      videos.add(video);
      video.muted = true;
      videoObserver?.observe(video);
    });
    scope.querySelectorAll('[data-rr-motion-toggle]').forEach(button => {
      if (button.dataset.ready) return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => {
        paused = !paused;
        try { sessionStorage.setItem('rr-motion-paused', String(paused)); } catch (_) {}
        updateMotion();
      });
    });
    if (revealObserver && !reduce.matches) {
      root.classList.add('rr-reveal-ready');
      scope.querySelectorAll('[data-rr-reveal]').forEach(el => revealObserver.observe(el));
    }
    scope.querySelectorAll('[data-rr-tabs]').forEach(group => {
      if (group.dataset.ready) return;
      group.dataset.ready = 'true';
      const tabs = [...group.querySelectorAll('[data-rr-tab]')];
      const panels = [...group.querySelectorAll('[data-rr-panel]')];
      if (!tabs.length) return;
      const tablist = group.querySelector('.rr-tabs');
      tablist.hidden = false;
      tablist.setAttribute('role', 'tablist');
      group.classList.add('rr-tabs-ready');
      const select = (tab, focus = false) => {
        tabs.forEach(button => {
          const active = button === tab;
          button.setAttribute('role', 'tab');
          button.setAttribute('aria-selected', String(active));
          button.tabIndex = active ? 0 : -1;
        });
        panels.forEach(panel => {
          panel.hidden = panel.dataset.rrPanel !== tab.dataset.rrTab;
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', 'rr-tab-' + panel.dataset.rrPanel);
        });
        if (focus) tab.focus();
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', event => {
          let target;
          if (event.key === 'ArrowRight') target = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') target = (index + tabs.length - 1) % tabs.length;
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = tabs.length - 1;
          if (target !== undefined) { event.preventDefault(); select(tabs[target], true); }
        });
      });
      select(tabs[0]);
    });
    updateMotion();
    updateScroll();
  };
  let ticking = false;
  const updateScroll = () => {
    ticking = false;
    if (reduce.matches || paused) return;
    const film = document.querySelector('.rr-hero__film');
    if (film) {
      const box = film.getBoundingClientRect();
      if (box.bottom > 0 && box.top < innerHeight) film.style.setProperty('--rr-film-shift', Math.max(-22, Math.min(22, -box.top * 0.045)) + 'px');
    }
    document.querySelectorAll('[data-rr-stage-visual]').forEach(el => {
      const box = el.parentElement.getBoundingClientRect();
      if (box.bottom < 0 || box.top > innerHeight) return;
      el.style.setProperty('--rr-stage-shift', Math.max(-20, Math.min(20, -box.top * 0.025)) + 'px');
    });
    document.querySelectorAll('.rr-story__image').forEach(el => {
      const box = el.getBoundingClientRect();
      if (box.bottom < 0 || box.top > innerHeight) return;
      const progress = (innerHeight / 2 - box.top) / innerHeight;
      el.style.setProperty('--rr-detail-shift', Math.max(-18, Math.min(18, progress * 26)) + 'px');
      el.style.setProperty('--rr-detail-rotation', (progress * 24 - 8) + 'deg');
    });
  };
  addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(updateScroll); } }, { passive: true });
  document.addEventListener('visibilitychange', updateVideos);
  reduce.addEventListener('change', event => { paused = event.matches; updateMotion(); });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-rr-stage], .rr-hero__film').forEach(scene => sceneObserver?.unobserve(scene));
    event.target.querySelectorAll('[data-rr-video]').forEach(video => { videoObserver?.unobserve(video); videos.delete(video); });
    event.target.querySelectorAll('[data-rr-reveal]').forEach(el => revealObserver?.unobserve(el));
    updateVideos();
  });
  init();
})();
